import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { FeedContext } from './FeedContext';

const token = process.env.REACT_APP_WSSR_API_TOKEN;
const LIVE_BROADCAST_ART = 'https://wssr.stream/other/album.png';

export const NowPlayingContext = createContext();

export const NowPlayingProvider = ({ children }) => {
  const [liveStreamData, setLiveStreamData] = useState(null);
  const [nowPlayingData, setNowPlayingData] = useState(null);
  const [playingNext, setPlayingNext] = useState(null);
  const [liveStreamUrl, setLiveStreamUrl] = useState("https://broadcast.wssr.stream/listen/wssr/radio.mp3");
  const [streamUrl, setStreamUrl] = useState("https://broadcast.wssr.stream/listen/wssr/radio.mp3");
  const [progressColor, setProgressColor] = useState("rgba(255, 102, 0, 1)");
  const [station, setStation] = useState("wssr");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLive, setIsLive] = useState(false); // New state for is_live
  const [currentTrackId, setCurrentTrackId] = useState(null);
  const [isLiveStream, setIsLiveStream] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false); // New state
  const [playbackLocked, setPlaybackLocked] = useState(false); // New state
  const [currentTrackIndex, setCurrentTrackIndex] = useState(null);
  const [trackQueue, setTrackQueue] = useState([]);
  const [loadingTrackId, setLoadingTrackId] = useState(null);
  const [currentStreamData, setCurrentStreamData] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [localElapsed, setLocalElapsed] = useState(0);
  let elapsedInterval = null; // Timer variable to track elapsed time updates
  const [loadingDelay, setLoadingDelay] = useState(false);
  const [liveStatuses, setLiveStatuses] = useState({ wssr: false, wssr_node_2: false });
  const [songHistory, setSongHistory] = useState(() => {
    const saved = localStorage.getItem('songHistoryData');
    return saved ? JSON.parse(saved) : [];
  });
  const [songHistoryStation, setSongHistoryStation] = useState(
    () => localStorage.getItem('songHistoryStation') || 'wssr'
  );

  const isLiveStreamRef = useRef(isLiveStream);
  const stationRef = useRef(station);
  const streamUrlRef = useRef(streamUrl);
  const liveStreamDataRef = useRef(null);
  const isPlayingRef = useRef(isPlaying);
  const [reconnectStreamTick, setReconnectStreamTick] = useState(0);
  // Caches the latest SSE snapshot for each station (including the one not currently
  // selected), so switching decks can seed nowPlayingData/liveStreamData instantly instead
  // of waiting for the next SSE tick for the newly-selected station.
  const stationDataRef = useRef({ wssr: null, wssr_node_2: null });


  const { feed } = useContext(FeedContext);

  useEffect(() => { isLiveStreamRef.current = isLiveStream; }, [isLiveStream]);
  useEffect(() => { stationRef.current = station; }, [station]);
  useEffect(() => { streamUrlRef.current = streamUrl; }, [streamUrl]);
  useEffect(() => { liveStreamDataRef.current = liveStreamData; }, [liveStreamData]);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);

  const startPlayback = () => {
    setIsPlaying(true);
  };

  const pauseTrack = () => {
    setIsPlaying(false);
  };



  
  const toggleStreamUrl = () => {
    if (playbackLocked) {
      return;
    }

    setPlaybackLocked(true);
    const wasPlaying = isPlaying;

    const newStation = station === "wssr" ? "wssr_node_2" : "wssr";
    const cached = stationDataRef.current[newStation];

    if (cached) {
      // The SSE connection already subscribes to both stations, so by the time the user
      // switches we likely already have a recent snapshot for the newly-selected one -
      // seed it immediately instead of showing a skeleton until the next SSE tick.
      setLiveStreamData(cached.song);
      setIsLive(cached.isLive);
      // Advance the cached elapsed by however long has passed since this snapshot was
      // captured, instead of resetting to 0 - keeps the progress bar from visibly resetting
      // and then jumping once the next real SSE message confirms the true position.
      const estimatedElapsed = cached.duration
        ? Math.min(cached.elapsed + (Date.now() - cached.fetchedAt) / 1000, cached.duration)
        : cached.elapsed;
      setNowPlayingData({
        title: cached.song.title || "Unknown Title",
        artist: cached.song.artist || "Unknown Artist",
        art: cached.isLive ? LIVE_BROADCAST_ART : (cached.song.art || "https://placehold.co/60"),
        duration: cached.duration,
        elapsed: estimatedElapsed,
      });
      setPlayingNext(cached.playingNext ? {
        title: cached.playingNext.title || "Unknown Title",
        artist: cached.playingNext.artist || "Unknown Artist",
        art: cached.playingNext.art || null,
      } : null);
      if (cached.songHistory) {
        setSongHistory(cached.songHistory);
        setSongHistoryStation(newStation);
        localStorage.setItem('songHistoryData', JSON.stringify(cached.songHistory));
        localStorage.setItem('songHistoryStation', newStation);
      }
      setIsLoading(false);
      setProgressColor(newStation === "wssr" ? "rgba(255, 102, 0, 1)" : "rgba(146, 171, 207, 1)");
    } else {
      // No snapshot cached yet for this station (e.g. switching before any SSE message for
      // it has arrived) - fall back to the previous wait-for-SSE behavior.
      setIsLoading(true);
      setProgressColor("#000");
      // isLive is keyed to whatever station is currently selected; reset it here so a
      // stale "the old station was live" value doesn't survive the switch and wrongly
      // suppress loading UI for the new station until the next SSE message confirms it.
      setIsLive(false);
      setNowPlayingData((prevData) => ({
        ...prevData,
        elapsed: 0,
      }));
    }

    const newStreamUrl =
      newStation === "wssr"
        ? "https://broadcast.wssr.stream/listen/wssr/radio.mp3"
        : "https://broadcast.wssr.stream/listen/wssr_node_2/radio.mp3";

    setStation(newStation);
    setStreamUrl(newStreamUrl);
    setLiveStreamUrl(newStreamUrl);
    setCurrentTrackId(null);
    setIsLiveStream(true);

    setTimeout(() => {
      if (wasPlaying) {
        setTimeout(() => {
          setIsPlaying(true);
          setPlaybackLocked(false);
        }, 200);
      } else {
        setPlaybackLocked(false);
      }
    }, 300);
  };
  
  
  
  



  const playSpecificTrack = async (trackUrl, trackData, trackId, trackIndex) => {
    try {
      const previousUrl = streamUrlRef.current;
      if (previousUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(previousUrl);

        // The track that owned this blob may still be cached with the now-dead URL in
        // trackQueue or the trackList passed in - clear it so navigating back to that
        // track later triggers a fresh re-fetch instead of trying to play a revoked blob.
        setTrackQueue((prevQueue) =>
          prevQueue.map((track) => (track.url === previousUrl ? { ...track, url: undefined } : track))
        );
        trackData?.trackList?.forEach((track) => {
          if (track.url === previousUrl) {
            delete track.url;
          }
        });
      }
      setLoadingTrackId(trackId);
  
      // Process track data
      const processedData = {
        id: trackId,
        title: trackData?.title || 'Unknown Title',
        artist: trackData?.artist || 'Unknown Artist',
        art: trackData?.art?.startsWith('http')
          ? trackData.art
          : `https://broadcast.wssr.stream${trackData.art || ''}`, // Prefix art URL if needed
        url: trackUrl,
        duration: trackData?.duration || 0,
        elapsed: 0,
        trackList: trackData?.trackList || [],
        path: trackData?.path?.startsWith('http')
          ? trackData.path
          : `https://broadcast.wssr.stream${trackData.path || ''}`, // Prefix path if needed
      };
  
      setTrackQueue((prevQueue) => {
        if (prevQueue.some((track) => track.path === trackId)) {
          return prevQueue; // Avoid duplicates
        }
        return [...prevQueue, { ...processedData, path: trackId }];
      });
  
      setStreamUrl(trackUrl);
      setNowPlayingData(processedData);
      setCurrentTrackId(trackId);
      setCurrentTrackIndex(trackIndex);
      setIsPlaying(true);
      setIsLiveStream(false);
    } catch (error) {
      console.error('Error during track playback preparation:', error);
    } finally {
      setLoadingTrackId(null);
    }
  };
  
  
  
  
  





// Utility function to clear revoked URLs
const clearRevokedBlobUrl = (track) => {
  if (track?.url?.startsWith('blob:')) {
    URL.revokeObjectURL(track.url); // Ensure it is revoked
    delete track.url; // Remove it from the track object
  }
};









  

const playPreviousTrack = async (trackList) => {
  if (currentTrackIndex > 0) {
      const previousIndex = currentTrackIndex - 1;
      let previousTrack = trackList[previousIndex];

      try {
          // Set loadingTrackId for progress indication
          setLoadingTrackId(previousTrack.id || previousTrack.path);

          // ShowTrackList episodes have no file path; their id doubles as the track identifier
          if (previousTrack.source === 'ShowTrackList' && !previousTrack?.path) {
              previousTrack = { ...previousTrack, path: previousTrack.id };
              trackList[previousIndex] = previousTrack;
          }

          if (!previousTrack?.path) {
              throw new Error('Invalid previous track path');
          }

          // Generate or reuse blob URL for the track
          let blobUrl = previousTrack.url || '';
          if (!blobUrl.startsWith('blob:')) {
              const trackUrl = previousTrack.links?.download.startsWith('http')
                  ? previousTrack.links.download
                  : `https://broadcast.wssr.stream${previousTrack.links.download}`;

              if (!trackUrl) {
                  throw new Error('Failed to resolve URL for the previous track');
              }

              const response = await fetch(trackUrl, { headers: { Authorization: token } });
              const blob = await response.blob();
              blobUrl = URL.createObjectURL(blob);
              previousTrack.url = blobUrl; // Cache blob URL
          }

          // Update current track state and play
          setCurrentTrackIndex(previousIndex);
          setCurrentTrackId(previousTrack.id); // Update currentTrackId
          playSpecificTrack(blobUrl, {
              title: previousTrack.media?.title || previousTrack.title || 'Unknown Title',
              artist: previousTrack.media?.artist || previousTrack.description_short || 'Unknown Artist',
              art: previousTrack.media?.art
                  ? `https://broadcast.wssr.stream${previousTrack.media.art}`
                  : previousTrack.art,
              duration: previousTrack.media?.length || previousTrack.duration || 0,
              trackList,
          }, previousTrack.path, previousIndex);
      } catch (error) {
          console.error('Error playing previous track:', error);
      } finally {
          // Clear loading state
          setLoadingTrackId(null);
      }
  } else {
      console.error('No previous track available');
  }
};







  
  
  
  
  
const playNextTrack = async (trackList) => {
  if (currentTrackIndex < trackList.length - 1) {
      const nextIndex = currentTrackIndex + 1;
      let nextTrack = trackList[nextIndex];

      try {
          // Set loadingTrackId to show progress
          setLoadingTrackId(nextTrack.id || nextTrack.path);

          // ShowTrackList episodes have no file path; their id doubles as the track identifier
          if (nextTrack.source === 'ShowTrackList' && !nextTrack?.path) {
              nextTrack = { ...nextTrack, path: nextTrack.id };
              trackList[nextIndex] = nextTrack;
          }

          if (!nextTrack?.path) {
              throw new Error('Invalid next track path');
          }

          // Generate or reuse blob URL for the track
          let blobUrl = nextTrack.url || '';
          if (!blobUrl.startsWith('blob:')) {
              const trackUrl = nextTrack.links?.download.startsWith('http')
                  ? nextTrack.links.download
                  : `https://broadcast.wssr.stream${nextTrack.links.download}`;

              if (!trackUrl) {
                  throw new Error('Failed to resolve URL for the next track');
              }

              const response = await fetch(trackUrl, { headers: { Authorization: token } });
              const blob = await response.blob();
              blobUrl = URL.createObjectURL(blob);
              nextTrack.url = blobUrl; // Cache blob URL
          }

          // Update current track state and play
          setCurrentTrackIndex(nextIndex);
          setCurrentTrackId(nextTrack.id); // Update currentTrackId
          playSpecificTrack(blobUrl, {
              title: nextTrack.media?.title || nextTrack.title || 'Unknown Title',
              artist: nextTrack.media?.artist || nextTrack.description_short || 'Unknown Artist',
              art: nextTrack.media?.art
                  ? `https://broadcast.wssr.stream${nextTrack.media.art}`
                  : nextTrack.art,
              duration: nextTrack.media?.length || nextTrack.duration || 0,
              trackList,
          }, nextTrack.path, nextIndex);
      } catch (error) {
          console.error('Error playing next track:', error);
      } finally {
          // Clear loading state
          setLoadingTrackId(null);
      }
  } else {
      setNowPlayingData((prevData) => ({
          ...prevData,
          elapsed: 0,
          //duration: 0,
      }));
      setIsPlaying(false);
  }
};












  

const fetchDirectoryTracks = async (path) => {
  try {
    const endpoint = `https://broadcast.wssr.stream/api/station/1/files/list?currentDirectory=${encodeURIComponent(path)}`;
    const response = await axios.get(endpoint, { headers: { Authorization: token } });
    return response.data.map((track) => ({
      ...track,
      fullArtUrl: track.media?.art ? `https://broadcast.wssr.stream${track.media.art}` : null,
    }));
  } catch (error) {
    console.error('Error fetching directory tracks:', error);
    throw error;
  }
};

  
  
  
  

useEffect(() => {
  const sseUri = `https://broadcast.wssr.stream/api/live/nowplaying/sse?cf_connect=${JSON.stringify({
    subs: { "station:wssr": {}, "station:wssr_node_2": {} },
  })}`;

  const sseRef = { current: null };

  const connectSSE = () => {
    if (sseRef.current) {
      sseRef.current.close();
    }

    const sse = new EventSource(sseUri);
    sseRef.current = sse;

    sse.onmessage = handleSSEMessage;

    sse.onerror = () => {
      console.error("SSE encountered an error, closing...");
      sse.close();
    };
  };

  const handleSSEMessage = (e) => {
    const data = JSON.parse(e.data);
    const npData = data?.pub?.data?.np;
    if (!npData) return;

    const messageStation = npData.station?.shortcode;
    if (!messageStation) return;

    const isLiveNow = npData.live?.is_live || false;
    setLiveStatuses((prev) => {
      if (prev[messageStation] === isLiveNow) return prev;
      return { ...prev, [messageStation]: isLiveNow };
    });

    // Cache a snapshot for whichever station this message is for - including the one not
    // currently selected - so switching decks can seed data instantly instead of waiting on
    // the next SSE tick for the newly-selected station.
    const cacheEntry = stationDataRef.current[messageStation] || {};
    const artUrl = isLiveNow ? LIVE_BROADCAST_ART : (npData.now_playing.song.art || null);
    stationDataRef.current[messageStation] = {
      song: npData.now_playing.song,
      duration: npData.now_playing.duration || 0,
      elapsed: npData.now_playing.elapsed || 0,
      fetchedAt: Date.now(),
      isLive: isLiveNow,
      playingNext: npData.playing_next?.song || null,
      // "Just Missed" history - cache it the same way so a switch doesn't have to wait on
      // the next SSE tick for the new station; no art prefetch here since a history list is
      // several entries and would multiply background image requests for no displayed benefit.
      songHistory: npData.song_history?.length > 0 ? npData.song_history : cacheEntry.songHistory,
      _lastPrefetchedArt: cacheEntry._lastPrefetchedArt,
    };

    // Warm the browser's image cache for this station's art so it's already
    // downloaded/decoded by the time the user switches to it.
    if (artUrl && artUrl !== cacheEntry._lastPrefetchedArt) {
      stationDataRef.current[messageStation]._lastPrefetchedArt = artUrl;
      new Image().src = artUrl;
    }

    if (messageStation !== stationRef.current) return;

    setLiveStreamData(npData.now_playing.song);
    setLiveStreamUrl(npData.station?.listen_url || '');
    setIsLive(isLiveNow);

    const newHistory = npData.song_history || [];
    if (newHistory.length > 0) {
      setSongHistory(newHistory);
      setSongHistoryStation(messageStation);
      localStorage.setItem('songHistoryData', JSON.stringify(newHistory));
      localStorage.setItem('songHistoryStation', messageStation);
    }

    if (isLiveStreamRef.current) {
      setNowPlayingData({
        title: npData.now_playing.song.title || "Unknown Title",
        artist: npData.now_playing.song.artist || "Unknown Artist",
        // AzuraCast keeps echoing the last AutoDJ track's art during a live broadcast,
        // since live sources have no reliable art field of their own - force the station
        // graphic instead so a stale track photo never gets attributed to the live show.
        art: isLiveNow ? LIVE_BROADCAST_ART : (npData.now_playing.song.art || "https://placehold.co/60"),
        duration: npData.now_playing.duration || 0,
        elapsed: npData.now_playing.elapsed || 0,
      });
      setProgressColor(messageStation === "wssr" ? "rgba(255, 102, 0, 1)" : "rgba(146, 171, 207, 1)");
      setIsLoading(false);
      setInitialLoadComplete(true);

      const nextSong = npData.playing_next?.song;
      setPlayingNext(nextSong ? {
        title: nextSong.title || "Unknown Title",
        artist: nextSong.artist || "Unknown Artist",
        art: nextSong.art || null,
      } : null);
    }
  };

  connectSSE();

  const handleOnline = () => {
    connectSSE();
    if (isLiveStreamRef.current && isPlayingRef.current) {
      setReconnectStreamTick((t) => t + 1);
    }
  };
  window.addEventListener('online', handleOnline);

  return () => {
    window.removeEventListener('online', handleOnline);
    if (sseRef.current) {
      sseRef.current.close();
    }
  };
}, []);








const setLiveStreamNowPlayingData = () => {
  // liveStreamDataRef only has the raw song (title/artist/art) - duration/elapsed live in the
  // per-station SSE cache. Hardcoding duration: 0 here made the progress bar compute 0/0 (NaN),
  // which the browser renders as a block-level div's default 100% width instead of 0%.
  const cached = stationDataRef.current[stationRef.current];
  if (!cached) return;
  const estimatedElapsed = cached.duration
    ? Math.min(cached.elapsed + (Date.now() - cached.fetchedAt) / 1000, cached.duration)
    : cached.elapsed;
  setNowPlayingData({
    title: cached.song.title || "Unknown Title",
    artist: cached.song.artist || "Unknown Artist",
    art: cached.isLive ? LIVE_BROADCAST_ART : (cached.song.art || "https://placehold.co/60"),
    duration: cached.duration,
    elapsed: estimatedElapsed,
  });
};





  
  
  
  
  
  
  



  return (
    <NowPlayingContext.Provider
      value={{
        station,
        liveStreamData,
        nowPlayingData,
        playingNext,
        streamUrl,
        progressColor,
        toggleStreamUrl,
        startPlayback,
        playSpecificTrack,
        pauseTrack,
        isPlaying,
        setIsPlaying,
        currentTrackId,
        setCurrentTrackId,
        setStreamUrl,
        setNowPlayingData,
        isLive, 
        isLiveStream,
        setIsLiveStream,
        isLoading: isLoading || loadingDelay,
        setIsLoading,
        initialLoadComplete, 
        currentTrackIndex,
        setCurrentTrackIndex,
        playPreviousTrack,
        playNextTrack,
        trackQueue,
        setTrackQueue,
        loadingTrackId,
        setLoadingTrackId,
        fetchDirectoryTracks,
        setLiveStreamNowPlayingData,
        liveStatuses,
        songHistory,
        songHistoryStation,
        reconnectStreamTick,
      }}
    >
      {children}
    </NowPlayingContext.Provider>
  );
};
